import React, { useState, useEffect, useMemo } from 'react';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import { useWishlist } from '../../context/WishlistContext';
import { MagereHeader, MagereFooter } from './MagereSharedLayout';

// Extracted components
import { WaveSeparator } from './components/WaveSeparator';
import { Hero } from './components/HeroSections';
import { CategoryCircles, ProductShowcase, GoogleReviews, BlogSection, RecentlyViewedSection } from './components/HomeSections';
import ShopView from './components/ShopView';
import FloatingCart from './components/FloatingCart';
import SeoHead from '../../components/SeoHead';

const MagereStorefront = ({
  store, template, products, categories, cmsPages,
  search, setSearch, selectedCategory, setSelectedCategory,
  sort, setSort, view, setView, addItem, itemCount, setIsOpen, storeSlug,
  cartItems, removeItem, updateQuantity, cartTotal, standalone = false,
  userType = 'particulier'
}) => {
  const id = useMemo(() => store?.identity || {}, [store]);
  const colors = useMemo(() => ({
    primary: id.primary_color || '#c2185b',
    dark: '#2b2b2b',
    darkFooter: '#1a1a1a',
    text: '#ffffff',
    accent: id.accent_color || '#ff6900',
  }), [id]);

  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;
  const isPro = userType === 'pro';

  const logoUrl = id.logo_light || '';
  const goCategory = (cid) => { setSelectedCategory(cid); setView('shop'); };
  const [featured, setFeatured] = useState({ featured: [], seasonal: [], promos: [] });
  const [expandedCats, setExpandedCats] = useState({});
  const [displayLimit, setDisplayLimit] = useState(24);
  const { recentlyViewed } = useRecentlyViewed(storeSlug);
  const { isInWishlist, toggleWishlist, fetchWishlistBySlug } = useWishlist();

  const parentCats = useMemo(() => categories.filter(c => !c.parent_id), [categories]);
  const API = process.env.REACT_APP_BACKEND_URL;

  /* Local search state */
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [floatingCartOpen, setFloatingCartOpen] = useState(false);
  const [productView, setProductView] = useState('grid');
  const [priceRange, setPriceRange] = useState([0, 9999]);
  const [homeTab, setHomeTab] = useState('bestsellers');

  useEffect(() => {
    if (search) { setSearchInput(search); setDebouncedSearch(search); }
  }, [search]);

  /* Debounce search input */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* Set max price from products */
  useEffect(() => {
    if (products.length > 0) {
      const max = Math.ceil(Math.max(...products.map(p => parseFloat(p.price) || 0)));
      setPriceRange(prev => [prev[0], prev[1] === 9999 ? max : prev[1]]);
    }
  }, [products]);

  /* Fetch CMS pages for footer */
  const [pages, setPages] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/pages`)
      .then(r => r.json())
      .then(d => setPages(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [storeSlug, API]);

  /* Fetch delivery info */
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/delivery-info`)
      .then(r => r.json())
      .then(d => setDeliveryInfo(d))
      .catch(() => {});
  }, [storeSlug, API]);

  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/featured`).then(r => r.json()).then(setFeatured).catch(() => {});
  }, [storeSlug, API]);

  /* SEO: Dynamic document title + meta */
  useEffect(() => {
    if (!store) return;
    const name = store?.identity?.company_name || store?.name || 'Boutique';
    const baseline = store?.identity?.hero_subtitle || 'Viande d\'exception';
    document.title = `${name} — ${baseline}`;
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', `${name} — ${baseline}. Boucherie artisanale, livraison à domicile.`);
    setMeta('property', 'og:title', `${name} — ${baseline}`);
    setMeta('property', 'og:description', `Boucherie artisanale d'exception. Viandes maturées, charcuteries, braseros. Livraison réfrigérée.`);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', name);
    if (store?.identity?.logo_url) setMeta('property', 'og:image', store.identity.logo_url);
  }, [store]);

  /* Reset display limit when changing filters */
  useEffect(() => { setDisplayLimit(24); }, [selectedCategory, debouncedSearch, sort, priceRange]);

  /* Fetch reviews */
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/reviews`)
      .then(r => r.json())
      .then(d => setReviews(d || []))
      .catch(() => {
        setReviews([
          { author: 'Pascal M.', rating: 5, text: 'Excellent rapport qualité prix, produits frais et de qualité.', date: 'Il y a 2 mois' },
          { author: 'Sophie L.', rating: 5, text: 'Super boucherie ! Viandes d\'une qualité exceptionnelle.', date: 'Il y a 3 mois' },
          { author: 'Jean-Pierre D.', rating: 5, text: 'Très bonne boucherie, large choix de viandes de qualité.', date: 'Il y a 1 mois' },
          { author: 'Marie C.', rating: 4, text: 'Bonne qualité de viande. Service sympathique.', date: 'Il y a 4 mois' },
        ]);
      });
  }, [storeSlug, API]);

  /* Fetch blog posts */
  const [blogPosts, setBlogPosts] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/blog?featured=true&limit=6`)
      .then(r => r.json())
      .then(d => {
        const posts = d.posts || [];
        if (posts.length === 0) {
          fetch(`${API}/api/s/${storeSlug}/blog?limit=3`)
            .then(r2 => r2.json())
            .then(d2 => setBlogPosts(d2.posts || []))
            .catch(() => {});
        } else {
          setBlogPosts(posts);
        }
      })
      .catch(() => {});
  }, [storeSlug, API]);

  /* Shared props for ProductCard components */
  const cardProps = {
    colors, categories, getLink, isInWishlist, toggleWishlist, store, addItem, storeSlug, isPro
  };

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden" data-testid="magere-storefront">
      <SeoHead
        title={`${store?.name || 'MAGERE Viandes'} — Boucherie traditionnelle Istres · Viandes d'exception, Kamados, Braséros`}
        description={`Boucherie ${store?.name || 'MAGERE Viandes'} à Istres (13800) — Bœuf, agneau, volaille, charcuterie maison. Livraison locale gratuite dès 100€. Spécialités barbecue, Kamados Big Green Egg, Braséros.`}
        image={store?.identity?.logo_url || store?.identity?.hero_image_url}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": typeof window !== 'undefined' ? window.location.origin : undefined,
            "name": store?.name || "MAGERE Viandes",
            "image": store?.identity?.logo_url,
            "description": "Boucherie traditionnelle à Istres — Viandes d'exception, charcuterie maison, Kamados et Braséros",
            "url": typeof window !== 'undefined' ? window.location.origin : undefined,
            "telephone": store?.identity?.phone || "+33490174267",
            "priceRange": "€€",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": store?.identity?.address || "45 rue de la boucherie",
              "addressLocality": store?.identity?.city || "Istres",
              "postalCode": store?.identity?.postal_code || "13800",
              "addressCountry": "FR",
            },
            "openingHoursSpecification": (() => {
              const dayMap = { lundi: 'Monday', mardi: 'Tuesday', mercredi: 'Wednesday', jeudi: 'Thursday', vendredi: 'Friday', samedi: 'Saturday', dimanche: 'Sunday' };
              const oh = store?.company?.opening_hours || {};
              const spec = [];
              Object.entries(dayMap).forEach(([fr, en]) => {
                const h = oh[fr];
                if (h && !h.closed && h.open && h.close) {
                  spec.push({ "@type": "OpeningHoursSpecification", "dayOfWeek": en, "opens": h.open, "closes": h.close });
                  if (h.open2 && h.close2) spec.push({ "@type": "OpeningHoursSpecification", "dayOfWeek": en, "opens": h.open2, "closes": h.close2 });
                }
              });
              return spec.length ? spec : [
                { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "08:00", "closes": "19:00" },
                { "@type": "OpeningHoursSpecification", "dayOfWeek": "Sunday", "opens": "08:00", "closes": "12:30" },
              ];
            })(),
            "sameAs": [
              store?.identity?.facebook_url,
              store?.identity?.instagram_url,
            ].filter(Boolean),
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": typeof window !== 'undefined' ? window.location.origin : undefined,
            "name": store?.name || "MAGERE Viandes",
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${typeof window !== 'undefined' ? window.location.origin : ''}/?search={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Jost:wght@300;400;500;600;700;900&family=EB+Garamond:ital,wght@0,400;1,400&display=swap" rel="stylesheet" />
      <MagereHeader
        store={store}
        categories={parentCats}
        onBoutiqueClick={() => setView('shop')}
        onCartClick={() => setFloatingCartOpen(prev => !prev)}
        onHomeClick={() => setView('home')}
        standalone={standalone}
      />
      <main className="flex-1" style={{ fontFamily: "'Jost', sans-serif" }}>
        {view === 'home' ? (
          <>
            <Hero id={id} colors={colors} setView={setView} />
            <CategoryCircles parentCats={parentCats} categories={categories} products={products} selectedCategory={selectedCategory} goCategory={goCategory} colors={colors} />
            <ProductShowcase featured={featured} homeTab={homeTab} setHomeTab={setHomeTab} colors={colors} setView={setView} cardProps={cardProps} />
            <WaveSeparator color="#faf9f7" />
            <GoogleReviews reviews={reviews} colors={colors} id={id} />
            <WaveSeparator color="#ffffff" />
            <BlogSection blogPosts={blogPosts} colors={colors} getLink={getLink} />
            <RecentlyViewedSection recentlyViewed={recentlyViewed} colors={colors} getLink={getLink} />
          </>
        ) : (
          <>
            <ShopView
              products={products} categories={categories} parentCats={parentCats} colors={colors}
              sort={sort} setSort={setSort} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
              searchInput={searchInput} setSearchInput={setSearchInput} debouncedSearch={debouncedSearch}
              displayLimit={displayLimit} setDisplayLimit={setDisplayLimit} featured={featured}
              id={id} goCategory={goCategory} getLink={getLink} addItem={addItem} storeSlug={storeSlug}
              isPro={isPro} isInWishlist={isInWishlist} toggleWishlist={toggleWishlist} store={store}
              priceRange={priceRange} setPriceRange={setPriceRange} productView={productView} setProductView={setProductView}
              expandedCats={expandedCats} setExpandedCats={setExpandedCats}
            />
            <RecentlyViewedSection recentlyViewed={recentlyViewed} colors={colors} getLink={getLink} />
          </>
        )}
      </main>
      <MagereFooter store={store} pages={pages} categories={parentCats} standalone={standalone} />

      <FloatingCart
        cartItems={cartItems} removeItem={removeItem} updateQuantity={updateQuantity} cartTotal={cartTotal} addItem={addItem}
        colors={colors} getLink={getLink} storeSlug={storeSlug} deliveryInfo={deliveryInfo} products={products}
        floatingCartOpen={floatingCartOpen} setFloatingCartOpen={setFloatingCartOpen}
      />

      <style>{`
        @keyframes waveSlow { 0%,100%{d:path("M0,40 C180,80 360,0 540,40 C720,80 900,0 1080,40 C1260,80 1440,0 1440,40 L1440,80 L0,80 Z")} 50%{d:path("M0,50 C180,10 360,70 540,30 C720,60 900,20 1080,50 C1260,10 1440,70 1440,50 L1440,80 L0,80 Z")} }
        @keyframes waveSlower { 0%,100%{d:path("M0,50 C200,20 400,70 600,40 C800,10 1000,60 1200,35 C1350,15 1440,50 1440,50 L1440,80 L0,80 Z")} 50%{d:path("M0,30 C200,60 400,10 600,50 C800,70 1000,20 1200,55 C1350,75 1440,30 1440,30 L1440,80 L0,80 Z")} }
        .animate-wave-slow { animation: waveSlow 8s ease-in-out infinite; }
        .animate-wave-slower { animation: waveSlower 12s ease-in-out infinite; }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        .animate-fadeInUp { animation: fadeInUp 0.8s ease-out; }
        @keyframes drawUnderline { from { width:0; opacity:0; } to { width:100%; opacity:1; } }
        .animate-drawUnderline { animation: drawUnderline 0.8s ease-out 1.4s forwards; }
        @keyframes scrollPromos { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-scrollPromos { animation: scrollPromos 25s linear infinite; display:flex; gap:16px; }
        .animate-scrollPromos:hover { animation-play-state: paused; }
        @keyframes marqueeScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-33.33%); } }
        .animate-marquee { animation: marqueeScroll 30s linear infinite; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default MagereStorefront;
